import * as amplitude from '@amplitude/analytics-browser';
import {
    AnalyticsApp,
    AnalyticsPlatform,
    parseStartParamAnalytics,
    resolvePremiumStatus,
    type PremiumStatusId,
} from '@focuslee/shared';
import { createContext, ReactNode, useCallback, useMemo, useRef } from 'react';
import { loginData } from '../client/apollo';
import { initAmplitude } from './analytics';
import { AnalyticsEventType } from './analyticsEvents';
import { MiniAppOpenedTracker } from './MiniAppOpenedTracker';
import { PageTracker } from './PageTracker';
import { UserIdentifyTracker } from './UserIdentifyTracker';
import { UserIdTracker } from './UserIdTracker';

export interface AnalyticsContextValue {
    recordPageView: (pageId: string, previousPagePath: string) => void;
    recordEvent: (eventType: string, eventData?: object) => void;
    setUserId: (userId: string) => void;
    identifyUser: (props: {
        locale?: string;
        premiumStatus?: PremiumStatusId;
        cohort?: string;
        inviteCode?: string;
        referrerUserId?: string;
    }) => void;
}

export const AnalyticsContext = createContext<AnalyticsContextValue>({
    recordPageView: () => {},
    recordEvent: () => {},
    setUserId: () => {},
    identifyUser: () => {},
});

export const AnalyticsProvider = ({ children }: { children: ReactNode }) => {
    const initialized = useRef(false);

    useMemo(() => {
        if (!initialized.current) {
            initialized.current = initAmplitude();
        }
    }, []);

    const recordPageView = useCallback((pageId: string, previousPagePath: string) => {
        amplitude.track(AnalyticsEventType.PAGE_VIEWED, {
            '[Amplitude] Page Path': pageId,
            '[Amplitude] Previous Page Location': previousPagePath,
        });
    }, []);

    const recordEvent = useCallback((eventType: string, eventData?: object) => {
        const authData = loginData();
        amplitude.track(eventType, {
            ...(eventData ?? {}),
            userId: authData?.userId,
            app: AnalyticsApp.focuslee,
            platform: AnalyticsPlatform.telegramMiniApp,
        });
    }, []);

    const setUserId = useCallback((userId: string) => {
        amplitude.setUserId(userId);
    }, []);

    const identifyUser = useCallback(
        (props: {
            locale?: string;
            premiumStatus?: PremiumStatusId;
            cohort?: string;
            inviteCode?: string;
            referrerUserId?: string;
        }) => {
            const identify = new amplitude.Identify();
            identify.set('app', AnalyticsApp.focuslee);
            identify.set('platform', AnalyticsPlatform.telegramMiniApp);
            if (props.locale) identify.set('locale', props.locale);
            if (props.premiumStatus) identify.set('premium_status', props.premiumStatus);
            if (props.cohort) identify.set('cohort', props.cohort);
            if (props.inviteCode) identify.set('invite_code', props.inviteCode);
            if (props.referrerUserId) identify.set('referrer_user_id', props.referrerUserId);
            amplitude.identify(identify);
        },
        [],
    );

    const value = useMemo(
        () => ({ recordPageView, recordEvent, setUserId, identifyUser }),
        [recordPageView, recordEvent, setUserId, identifyUser],
    );

    return (
        <AnalyticsContext.Provider value={value}>
            <PageTracker />
            <UserIdTracker setUserId={setUserId} />
            <UserIdentifyTracker identifyUser={identifyUser} />
            <MiniAppOpenedTracker recordEvent={recordEvent} />
            {children}
        </AnalyticsContext.Provider>
    );
};

export { parseStartParamAnalytics, resolvePremiumStatus };
